import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertTriangle, Brain } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { supabase } from '@/lib/supabase'
import { toast } from '@/hooks/use-toast'
import { calculateBotStatistics, type BotStatistics } from '@/services/BotStatisticsService'
import { PatternUpdateService, PatternUpdate } from '@/services/pattern-update-service'
import { BotCard } from './BotCard'
import { BotDetailDialog } from './BotDetailDialog'

interface BotWithStats {
  id: string
  name: string
  win_threshold?: number
  max_sequence_length?: number
  pattern_processing_enabled?: boolean
  linked_trading_file_id?: string
  min_pattern_length?: number
  max_pattern_length?: number
  min_pattern_frequency?: number
  statistics: BotStatistics
  pattern_updates?: PatternUpdate[]
}

export const RealTimePredictionsMonitor = () => {
  // Force cache refresh - grid layout implementation
  const [predictions, setPredictions] = useState<BotWithStats[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [repredictingBots, setRepredictingBots] = useState<Set<string>>(new Set())
  const [selectedBot, setSelectedBot] = useState<BotWithStats | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const fetchPredictions = async () => {
    try {
      // Get all bots with current predictions
      const { data: bots } = await supabase
        .from('bots')
        .select('id, name, win_threshold, max_sequence_length, pattern_processing_enabled, linked_trading_file_id, min_pattern_length, max_pattern_length, min_pattern_frequency')
        .not('id', 'is', null)

      if (bots) {
        // Calculate statistics for each bot using the new service
        const botsWithStats = await Promise.all(
          bots.map(async (bot) => {
            const statistics = await calculateBotStatistics(bot.id)
            const pattern_updates = await PatternUpdateService.getPatternUpdatesForBot(bot.id, 5)
            return {
              ...bot,
              statistics,
              pattern_updates
            }
          })
        )
        
        // Filter out bots with no current prediction
        const activeBots = botsWithStats.filter(bot => bot.statistics.current_prediction)
        
        setPredictions(activeBots)
        setLastUpdated(new Date())
      }
    } catch (error) {
      console.error('Error fetching predictions:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchPredictions()
    const interval = setInterval(fetchPredictions, 1000) // Real-time updates every 1 second
    return () => clearInterval(interval)
  }, [])

  const handleSettingsUpdate = (
    botId: string, 
    winThreshold: number, 
    maxSequenceLength: number,
    minPatternLength: number,
    maxPatternLength: number,
    minPatternFrequency: number
  ) => {
    setPredictions(prevPredictions => 
      prevPredictions.map(bot => 
        bot.id === botId 
          ? { 
              ...bot, 
              win_threshold: winThreshold, 
              max_sequence_length: maxSequenceLength,
              min_pattern_length: minPatternLength,
              max_pattern_length: maxPatternLength,
              min_pattern_frequency: minPatternFrequency
            }
          : bot
      )
    )
  }

  const handlePatternSettingsUpdate = (botId: string, enabled: boolean) => {
    setPredictions(prevPredictions => 
      prevPredictions.map(bot => 
        bot.id === botId 
          ? { ...bot, pattern_processing_enabled: enabled }
          : bot
      )
    )
  }

  const handleRePrediction = async (bot: BotWithStats) => {
    if (!bot.statistics.analyzed_sequence) {
      toast({
        title: "Error",
        description: "No pattern available for re-prediction",
        variant: "destructive"
      })
      return
    }

    if (!bot.linked_trading_file_id) {
      toast({
        title: "Error", 
        description: "No linked trading file found for this bot",
        variant: "destructive"
      })
      return
    }

    setRepredictingBots(prev => new Set(prev).add(bot.id))
    
    try {
      const { data, error } = await supabase.functions.invoke('re-prediction', {
        body: {
          botIds: [bot.id]
        }
      })

      if (error) {
        console.error('Re-prediction error:', error)
        toast({
          title: "Error",
          description: error.message || 'Failed to generate re-prediction',
          variant: "destructive"
        })
        return
      }

      if (!data?.success) {
        toast({
          title: "Error",
          description: 'Re-prediction failed',
          variant: "destructive"
        })
        return
      }

      // Refresh predictions to show updated results
      await fetchPredictions()
      toast({
        title: "Success",
        description: `Re-prediction completed for ${bot.name}`
      })
    } catch (error: any) {
      console.error('Re-prediction error:', error)
      toast({
        title: "Error",
        description: error.message || 'Failed to generate re-prediction',
        variant: "destructive"
      })
    } finally {
      setRepredictingBots(prev => {
        const newSet = new Set(prev)
        newSet.delete(bot.id)
        return newSet
      })
    }
  }

  const handleBotCardClick = (bot: BotWithStats) => {
    setSelectedBot(bot)
    setIsDialogOpen(true)
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setSelectedBot(null)
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Real-time Predictions Monitor</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          Real-time Predictions Monitor
        </CardTitle>
        <CardDescription>
          Monitor real-time predictions and accuracy for active bots
        </CardDescription>
      </CardHeader>
      <CardContent>
        {predictions.length === 0 ? (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              No active predictions found. Predictions appear when bots are linked and generating forecasts.
            </AlertDescription>
          </Alert>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-2 sm:gap-3">
            {predictions
              .sort((a, b) => b.statistics.accuracy_rate - a.statistics.accuracy_rate)
              .map((bot) => {
              if (!bot.statistics.current_prediction) return null
              
              return (
                <BotCard
                  key={bot.id}
                  id={bot.id}
                  name={bot.name}
                  statistics={bot.statistics}
                  onClick={() => handleBotCardClick(bot)}
                />
              )
            })}
          </div>
        )}

        <BotDetailDialog
          bot={selectedBot}
          isOpen={isDialogOpen}
          onClose={handleDialogClose}
          onSettingsUpdate={handleSettingsUpdate}
          onPatternSettingsUpdate={handlePatternSettingsUpdate}
          onRePrediction={handleRePrediction}
          repredictingBots={repredictingBots}
        />
      </CardContent>
    </Card>
  )
}